import {showNotification} from 'platform/components';

import {useRef, useState} from 'react';

import {isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  SaveSaleVehicleFinancingBody,
  VehicleFinancingType,
  useSaveSaleVehicleFinancingMutation,
} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {FinancingDrafts} from '../types/FinancingDrafts';
import {PendingEditor, PendingEditorChangeHandler} from '../types/PendingEditor';
import {areFinancingBodiesEqual} from '../utils/areFinancingBodiesEqual';
import {findInvalidFinancingMoneyKey} from '../utils/findInvalidFinancingMoneyKey';
import {hasValidActiveFinancingCell} from '../utils/hasValidActiveFinancingCell';
import {DraftSaveRequest, useQueuedDraftSave} from './useQueuedDraftSave';
import {useStablePendingEditor} from './useStablePendingEditor';

const FINANCING_TYPES: VehicleFinancingType[] = ['STANDARD', 'BALLOON'];

interface UseVehicleFinancingDraftsParams {
  saleVehicleId: string;
  activeType: VehicleFinancingType;
  /** Resolves the persisted body a draft of that type is compared against. */
  getPersistedBody: (type: VehicleFinancingType) => SaveSaleVehicleFinancingBody | null;
  /** Whether a type without a persisted configuration may be saved. */
  isCreateAllowed: boolean;
  /** Whether a type with a persisted configuration may be saved. */
  isUpdateAllowed: boolean;
  onPendingChange?: PendingEditorChangeHandler;
  onSavingChange?: (isSaving: boolean) => void;
  /** Called with the financing type whose draft was refused, so its editors can be shown. */
  onValidationError: (type: VehicleFinancingType) => void;
}

interface VehicleFinancingDrafts {
  drafts: FinancingDrafts;
  activeDraft: SaveSaleVehicleFinancingBody | undefined;
  activeSelectionErrorType: VehicleFinancingType | null;
  /** The financing type whose manual draft was refused for having no provider. */
  financialInstitutionErrorType: VehicleFinancingType | null;
  isSaving: boolean;
  /** `true` while any financing type holds an unsaved draft, not only the active one. */
  isDirty: boolean;
  /** The drafts as of the latest publish, safe to read from asynchronous continuations. */
  getDrafts: () => FinancingDrafts;
  setActiveDraft: (nextBody: SaveSaleVehicleFinancingBody) => void;
  applyDraft: (type: VehicleFinancingType, nextBody: SaveSaleVehicleFinancingBody) => void;
  discardActiveDraft: () => void;
  discardAllDrafts: () => void;
  /** Saves every dirty draft, so success never leaves an unsaved draft behind. */
  saveDirtyDrafts: () => Promise<boolean>;
  handleConfigurationPendingChange: (editor: PendingEditor | null) => void;
}

/**
 * The financing draft engine: one unsaved body per financing type, the dirty diff against the
 * persisted configuration, the order the dirty bodies are sent in, and the pending editor published
 * to the surrounding page. `useQueuedDraftSave` owns the save attempt around those requests.
 */
export function useVehicleFinancingDrafts(
  params: UseVehicleFinancingDraftsParams
): VehicleFinancingDrafts {
  const [drafts, setDrafts] = useState<FinancingDrafts>({});
  const [activeSelectionErrorType, setActiveSelectionErrorType] =
    useState<VehicleFinancingType | null>(null);
  const [financialInstitutionErrorType, setFinancialInstitutionErrorType] =
    useState<VehicleFinancingType | null>(null);
  const draftsRef = useRef(drafts);
  const configurationPendingEditorRef = useRef<PendingEditor | null>(null);
  const [saveFinancing, saveStatus] = useSaveSaleVehicleFinancingMutation();
  const panelPendingEditor = useStablePendingEditor(params.onPendingChange);
  const saveDirtyDrafts = useQueuedDraftSave({
    prepareRequest: prepareDraftsRequest,
    onSavingChange: params.onSavingChange,
  });

  function publishDrafts(
    nextDrafts: FinancingDrafts,
    configurationPendingEditor = configurationPendingEditorRef.current
  ) {
    const dirtyDrafts = FINANCING_TYPES.filter((type) => {
      const nextDraft = nextDrafts[type];
      const persistedDraft = params.getPersistedBody(type);

      return (
        nextDraft !== undefined &&
        (persistedDraft === null || !areFinancingBodiesEqual(persistedDraft, nextDraft))
      );
    }).reduce<FinancingDrafts>((result, type) => {
      const nextDraft = nextDrafts[type];

      return nextDraft ? {...result, [type]: nextDraft} : result;
    }, {});

    draftsRef.current = dirtyDrafts;
    setDrafts(dirtyDrafts);
    publishPanelPendingEditor(dirtyDrafts, configurationPendingEditor);
  }

  function publishPanelPendingEditor(
    pendingDrafts: FinancingDrafts,
    configurationPendingEditor: PendingEditor | null
  ) {
    const hasFinancingDraft = FINANCING_TYPES.some((type) => pendingDrafts[type] !== undefined);

    panelPendingEditor.publishPendingEditor(
      hasFinancingDraft || isNotNil(configurationPendingEditor)
        ? {
            save: async () => {
              // Ordered validation steps, each with its own notification; kept as early returns
              // on purpose, the order is the order the user sees the messages in.
              const typeWithoutProvider = findManualFinancingTypeWithoutProvider(draftsRef.current);

              if (isNotNil(typeWithoutProvider)) {
                showFinancialInstitutionError(typeWithoutProvider);
                return false;
              }

              const invalidType = findFinancingTypeWithoutActiveVariant(draftsRef.current);

              if (isNotNil(invalidType)) {
                showActiveSelectionError(invalidType);
                return false;
              }

              if (
                isNotNil(configurationPendingEditor) &&
                !(await configurationPendingEditor.save())
              ) {
                return false;
              }

              return saveDirtyDrafts();
            },
            discard: () => {
              configurationPendingEditor?.discard();
              configurationPendingEditorRef.current = null;
              setFinancialInstitutionErrorType(null);
              publishDrafts({}, null);
            },
          }
        : null
    );
  }

  function handleConfigurationPendingChange(editor: PendingEditor | null) {
    configurationPendingEditorRef.current = editor;
    publishPanelPendingEditor(draftsRef.current, editor);
  }

  /**
   * Describes what a save attempt has to send, from the drafts as they stand when its turn comes.
   * A manual draft without a provider, a draft whose active variant is missing, or one that carries
   * a money override the endpoint would reject, rejects the whole attempt: the requests are sent
   * one after another, so letting an invalid body reach the endpoint would abandon the other
   * financing type's body with it.
   */
  function prepareDraftsRequest(): DraftSaveRequest {
    const pendingDrafts = draftsRef.current;
    // Ordered validation steps kept as early returns on purpose: permission, bank, active cell,
    // money overrides. Each refusal shows its own message, so the order is what the user sees.
    // Defence in depth behind the disabled editors: a save requested without the write permission
    // is refused here, so no programmatic call can reach the endpoint.
    const forbiddenType = FINANCING_TYPES.find(
      (type) => isNotNil(pendingDrafts[type]) && !isDraftSaveAllowed(type)
    );

    if (isNotNil(forbiddenType)) {
      return {type: 'REJECTED'};
    }

    const typeWithoutProvider = findManualFinancingTypeWithoutProvider(pendingDrafts);

    if (isNotNil(typeWithoutProvider)) {
      showFinancialInstitutionError(typeWithoutProvider);
      return {type: 'REJECTED'};
    }

    const invalidType = findFinancingTypeWithoutActiveVariant(pendingDrafts);

    if (isNotNil(invalidType)) {
      showActiveSelectionError(invalidType);
      return {type: 'REJECTED'};
    }

    const invalidMoneyType = findFinancingTypeWithInvalidMoney(pendingDrafts);

    if (isNotNil(invalidMoneyType)) {
      showMoneyOverrideError(invalidMoneyType);
      return {type: 'REJECTED'};
    }

    const bodies = FINANCING_TYPES.flatMap((type) => {
      const pendingBody = pendingDrafts[type];

      return isNotNil(pendingBody) ? [pendingBody] : [];
    });

    return bodies.length === 0
      ? {type: 'NOTHING_TO_SEND'}
      : {type: 'SEND', send: () => sendDrafts(bodies)};
  }

  function sendDrafts(bodies: SaveSaleVehicleFinancingBody[]) {
    return bodies
      .reduce<Promise<void>>(
        (saveSequence, pendingBody) =>
          saveSequence.then(async () => {
            await saveFinancing({
              saleVehicleId: params.saleVehicleId,
              body: pendingBody,
            }).unwrap();

            // Keep an edit made while the request was in flight: only the saved draft is cleared.
            const currentDraft = draftsRef.current[pendingBody.type];

            if (currentDraft && areFinancingBodiesEqual(currentDraft, pendingBody)) {
              const nextDrafts = {...draftsRef.current};
              delete nextDrafts[pendingBody.type];
              publishDrafts(nextDrafts);
            }
          }),
        Promise.resolve()
      )
      .then(() => publishDrafts(draftsRef.current));
  }

  function isDraftSaveAllowed(type: VehicleFinancingType) {
    return isNotNil(params.getPersistedBody(type))
      ? params.isUpdateAllowed
      : params.isCreateAllowed;
  }

  /** The backend accepts a manual configuration without a provider, but cannot offer it anywhere. */
  function findManualFinancingTypeWithoutProvider(pendingDrafts: FinancingDrafts) {
    return FINANCING_TYPES.find((type) => {
      const pendingBody = pendingDrafts[type];

      return pendingBody?.mode === 'MANUAL' && isNilOrEmpty(pendingBody.financialInstitutionCodeId);
    });
  }

  function findFinancingTypeWithoutActiveVariant(pendingDrafts: FinancingDrafts) {
    return FINANCING_TYPES.find((type) => {
      const pendingBody = pendingDrafts[type];

      return pendingBody !== undefined && !hasValidActiveFinancingCell(pendingBody);
    });
  }

  function findFinancingTypeWithInvalidMoney(pendingDrafts: FinancingDrafts) {
    return FINANCING_TYPES.find((type) => {
      const pendingBody = pendingDrafts[type];

      return isNotNil(pendingBody) && isNotNil(findInvalidFinancingMoneyKey(pendingBody));
    });
  }

  function showFinancialInstitutionError(type: VehicleFinancingType) {
    setFinancialInstitutionErrorType(type);
    params.onValidationError(type);
  }

  function showActiveSelectionError(type: VehicleFinancingType) {
    setActiveSelectionErrorType(type);
    params.onValidationError(type);
  }

  /**
   * The offending input already shows its own message, so the notification only has to say that
   * something is invalid — the switch to that financing type is what puts the message on screen.
   */
  function showMoneyOverrideError(type: VehicleFinancingType) {
    params.onValidationError(type);
    showNotification.error(i18n.t('general.errors.mixed.invalid'));
  }

  // A draft that has a bank, was replaced by another body (a template, a fresh manual draft) or
  // was discarded no longer carries the refusal, so a new manual draft of that type does not open
  // with the bank already marked as missing.
  const clearFinancialInstitutionError = (type: VehicleFinancingType) =>
    setFinancialInstitutionErrorType((errorType) => (errorType === type ? null : errorType));

  const clearActiveFinancialInstitutionError = () =>
    clearFinancialInstitutionError(params.activeType);

  const setActiveDraft = (nextBody: SaveSaleVehicleFinancingBody) => {
    if (hasValidActiveFinancingCell(nextBody)) {
      setActiveSelectionErrorType((errorType) =>
        errorType === params.activeType ? null : errorType
      );
    }

    if (isNotNilOrEmpty(nextBody.financialInstitutionCodeId)) {
      clearActiveFinancialInstitutionError();
    }

    publishDrafts({
      ...draftsRef.current,
      [params.activeType]: nextBody,
    });
  };

  const applyDraft = (type: VehicleFinancingType, nextBody: SaveSaleVehicleFinancingBody) => {
    clearFinancialInstitutionError(type);
    publishDrafts({
      ...draftsRef.current,
      [type]: nextBody,
    });
  };

  const discardActiveDraft = () => {
    const nextDrafts = {...draftsRef.current};
    delete nextDrafts[params.activeType];
    clearActiveFinancialInstitutionError();
    publishDrafts(nextDrafts);
  };

  const discardAllDrafts = () => {
    setFinancialInstitutionErrorType(null);
    publishDrafts({});
  };

  return {
    drafts,
    activeDraft: drafts[params.activeType],
    activeSelectionErrorType,
    financialInstitutionErrorType,
    isSaving: saveStatus.isLoading,
    isDirty: FINANCING_TYPES.some((type) => drafts[type] !== undefined),
    getDrafts: () => draftsRef.current,
    setActiveDraft,
    applyDraft,
    discardActiveDraft,
    discardAllDrafts,
    saveDirtyDrafts,
    handleConfigurationPendingChange,
  };
}
